import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowRight, Sparkles, FileText, Activity, Map as MapIcon, ShieldCheck, Zap } from "lucide-react";

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white text-zinc-950">
      {/* Top bar */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-30">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold tracking-tighter">vincularedu</span>
            <span className="text-2xl font-medium text-zinc-400">.</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#producto" className="text-zinc-600 hover:text-zinc-950 transition-colors">{t("landing.section_producto")}</a>
            <a href="#flujo" className="text-zinc-600 hover:text-zinc-950 transition-colors">{t("landing.section_flujo")}</a>
            <a href="#para-quien" className="text-zinc-600 hover:text-zinc-950 transition-colors">{t("landing.section_for")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/demo"
              className="hidden sm:inline-flex text-sm border border-zinc-300 px-4 py-2 hover:bg-zinc-50"
              data-testid="header-demo"
            >
              {t("nav.probar_demo")}
            </Link>
            <Link
              to="/login"
              className="text-sm bg-zinc-950 text-white px-4 py-2 hover:bg-zinc-800 inline-flex items-center gap-1.5"
              data-testid="header-login"
            >
              {t("nav.ingresar")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="section-marker mb-6">{t("landing.intel_tagline")}</div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.02]">
              {t("landing.hero_q1")}
              <br />
              <span className="serif-em">{t("landing.hero_q2")}</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
              {t("landing.hero_desc")}{" "}
              <span className="serif-em text-zinc-950">{t("landing.hero_desc_em")}</span>{" "}
              {t("landing.hero_desc_after")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/demo"
                className="bg-zinc-950 text-white px-6 py-4 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="hero-cta-demo"
              >
                <Sparkles size={18} strokeWidth={1.5} />
                {t("landing.cta_demo")}
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <Link
                to="/login"
                className="border border-zinc-950 px-6 py-4 font-medium hover:bg-zinc-50 inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-login"
              >
                {t("landing.cta_login")}
              </Link>
            </div>
            <p className="mt-4 text-xs text-zinc-500">{t("landing.demo_disclaimer")}</p>
          </div>

          {/* Right column: numbers grid */}
          <div className="border border-zinc-200 p-8 sm:p-10 lg:p-12 bg-zinc-50">
            <div className="section-marker mb-8">{t("landing.discover")}</div>
            <div className="grid grid-cols-2 gap-8 lg:gap-10">
              <div>
                <div className="kpi-number text-5xl sm:text-6xl">247</div>
                <div className="section-marker mt-2">{t("landing.convenios_firmados")}</div>
              </div>
              <div>
                <div className="kpi-number text-5xl sm:text-6xl text-emerald-700">38</div>
                <div className="section-marker mt-2">{t("landing.activos_verdad")}</div>
              </div>
              <div>
                <div className="kpi-number text-5xl sm:text-6xl text-amber-600">22</div>
                <div className="section-marker mt-2">{t("landing.por_vencer_90d")}</div>
              </div>
              <div>
                <div className="kpi-number text-5xl sm:text-6xl text-zinc-400">119</div>
                <div className="section-marker mt-2">{t("landing.sin_actividad")}</div>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-zinc-200">
              <div className="text-sm text-zinc-600 leading-relaxed">
                <span className="serif-em text-zinc-950">{t("landing.quote")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="producto" className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24">
          <div className="section-marker mb-4">{t("landing.section_producto_kicker")}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter max-w-3xl">
            {t("landing.section_producto_h")} <span className="serif-em">{t("landing.section_producto_em")}</span>.
          </h2>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200">
            <FeatureCard icon={Sparkles} num="01" title={t("landing.feat_ocr_t")} desc={t("landing.feat_ocr_d")} />
            <FeatureCard icon={Activity} num="02" title={t("landing.feat_acc_t")} desc={t("landing.feat_acc_d")} />
            <FeatureCard icon={MapIcon} num="03" title={t("landing.feat_dash_t")} desc={t("landing.feat_dash_d")} />
            <FeatureCard icon={FileText} num="04" title={t("landing.feat_vig_t")} desc={t("landing.feat_vig_d")} />
            <FeatureCard icon={ShieldCheck} num="05" title={t("landing.feat_roles_t")} desc={t("landing.feat_roles_d")} />
            <FeatureCard icon={Zap} num="06" title={t("landing.feat_conf_t")} desc={t("landing.feat_conf_d")} />
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="flujo" className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24">
          <div className="section-marker mb-4">{t("landing.section_flujo_kicker")}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter max-w-3xl">
            {t("landing.section_flujo_h")} <span className="serif-em">{t("landing.section_flujo_em")}</span>.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: t("landing.flujo_1_t"), d: t("landing.flujo_1_d") },
              { n: "02", t: t("landing.flujo_2_t"), d: t("landing.flujo_2_d") },
              { n: "03", t: t("landing.flujo_3_t"), d: t("landing.flujo_3_d") },
              { n: "04", t: t("landing.flujo_4_t"), d: t("landing.flujo_4_d") },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-zinc-200 p-6 sm:p-8">
                <div className="section-marker mb-3">{s.n}</div>
                <h3 className="text-xl font-medium tracking-tight mb-3">{s.t}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quién */}
      <section id="para-quien" className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="section-marker mb-4">{t("landing.section_for_kicker")}</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter">
              {t("landing.section_for_h")} <span className="serif-em">{t("landing.section_for_em")}</span> {t("landing.section_for_h2")}
            </h2>
            <p className="mt-6 text-zinc-600 leading-relaxed max-w-xl">
              {t("landing.section_for_desc")}
            </p>
          </div>
          <div className="space-y-4">
            {[
              { rol: t("landing.p1_rol"), v: t("landing.p1_v") },
              { rol: t("landing.p2_rol"), v: t("landing.p2_v") },
              { rol: t("landing.p3_rol"), v: t("landing.p3_v") },
            ].map((p) => (
              <div key={p.rol} className="border border-zinc-200 p-5 sm:p-6 flex gap-4">
                <div className="kpi-number text-3xl text-zinc-300 shrink-0">→</div>
                <div>
                  <div className="font-medium">{p.rol}</div>
                  <div className="text-sm text-zinc-600 mt-1">{p.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="section-marker !text-zinc-500 mb-6">{t("landing.final_cta_kicker")}</div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
              {t("landing.final_cta_h1")} <span className="serif-em">{t("landing.final_cta_em")}</span> {t("landing.final_cta_h2")} <br className="hidden sm:block" />
              {t("landing.final_cta_h3")}
            </h2>
            <p className="mt-8 text-lg text-zinc-300 leading-relaxed max-w-xl">
              {t("landing.final_cta_desc")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/demo"
                className="bg-white text-zinc-950 px-6 py-4 font-medium hover:bg-zinc-100 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="footer-cta-demo"
              >
                <Sparkles size={18} strokeWidth={1.5} />
                {t("nav.probar_demo")}
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <Link
                to="/login"
                className="border border-zinc-700 text-white px-6 py-4 font-medium hover:bg-zinc-900 inline-flex items-center justify-center gap-2"
                data-testid="footer-cta-login"
              >
                {t("nav.acceso_clientes")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-500 border-t border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm">
          <div className="flex items-baseline gap-1 text-white">
            <span className="text-xl font-semibold tracking-tighter">vincularedu</span>
            <span className="text-xl font-medium text-zinc-600">.</span>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} vincularedu · {t("landing.footer_tag")}</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, num, title, desc }) {
  return (
    <div className="bg-white p-6 sm:p-8 hover:bg-zinc-50 transition-colors">
      <div className="flex items-start justify-between mb-6">
        <Icon size={28} strokeWidth={1.5} className="text-zinc-950" />
        <div className="section-marker">{num}</div>
      </div>
      <h3 className="text-xl sm:text-2xl font-medium tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
    </div>
  );
}
